import { LucideIcon } from 'components/LucideIcon'
import { useGlobalSearch } from 'features/search/hooks/useGlobalSearch'
import { GlobalSearchOptions } from 'features/search/types'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { GeckoEntry } from 'types'

import { CategoryFilters } from './CategoryFilters'
import { SearchResultItem } from './SearchResultItem'

interface GlobalSearchBarProps {
  siteID?: string
  orgID?: string
  onResultClick?: (entry: GeckoEntry) => void
  options?: GlobalSearchOptions
  placeholder?: string
  autoFocus?: boolean
  // When this value changes, the input will be cleared and blurred
  resetSignal?: unknown
  // Style customization props
  containerStyles?: {
    backgroundColor?: string
    borderColor?: string
    borderRadius?: string
    height?: string
    focusBorderColor?: string
  }
  inputStyles?: {
    padding?: string
    fontSize?: string
    color?: string
    placeholderColor?: string
  }
}

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`

const SearchInputContainer = styled.div<{
  $backgroundColor?: string
  $borderColor?: string
  $borderRadius?: string
  $height?: string
  $focusBorderColor?: string
}>`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid ${({ $borderColor }) => $borderColor ?? '#3a3a3a'};
  border-radius: ${({ $borderRadius }) => $borderRadius ?? '0.5rem'};
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? '#0a0a0a'};
  height: ${({ $height }) => $height ?? 'auto'};
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: ${({ $focusBorderColor, $borderColor }) =>
      $focusBorderColor ?? $borderColor ?? '#4a4a4a'};
  }
`

const SearchInput = styled.input<{
  $padding?: string
  $fontSize?: string
  $color?: string
  $placeholderColor?: string
}>`
  width: 100%;
  padding: ${({ $padding }) => $padding ?? '1rem 1.25rem'};
  border: none;
  background: transparent;
  font-size: ${({ $fontSize }) => $fontSize ?? '1rem'};
  color: ${({ $color }) => $color ?? '#ffffff'};
  outline: none;

  &::placeholder {
    color: ${({ $placeholderColor }) => $placeholderColor ?? '#666666'};
  }
`

const ClearButton = styled.button`
  position: absolute;
  right: 1rem;
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: #999999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #ffffff;
  }
`

const LoadingSpinner = styled.div`
  position: absolute;
  right: 1rem;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #3a3a3a;
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const ResultsDropdown = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  max-height: 600px;
  overflow-y: auto;
  background-color: #1a1a1a;
  border: 1px solid #3a3a3a;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 100000;
  display: ${(props) => (props.$isVisible ? 'block' : 'none')};
`

const ResultsContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
`

const ResultItemWrapper = styled.div<{ $isSelected: boolean }>`
  border-radius: 0.375rem;
  background-color: ${(props) =>
    props.$isSelected ? '#2a2a2a' : 'transparent'};
  border: 2px solid
    ${(props) => (props.$isSelected ? '#ff8c00' : 'transparent')};
  transition: all 0.15s ease;

  &:hover {
    background-color: #2a2a2a;
  }
`

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #999999;
  font-size: 0.875rem;
`

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  siteID,
  orgID,
  onResultClick,
  options,
  placeholder = 'Search the site...',
  autoFocus = false,
  resetSignal,
  containerStyles,
  inputStyles,
}) => {
  const { theme } = useGeckoTheme()
  const [searchInput, setSearchInput] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<(HTMLDivElement | null)[]>([])

  const {
    debouncedSearchTerm,
    isDebouncing,
    isLoading,
    counts,
    filteredResults,
    activeFilters,
    toggleFilter,
  } = useGlobalSearch(searchInput, siteID, orgID, options)

  const showResults =
    isFocused &&
    debouncedSearchTerm !== undefined &&
    debouncedSearchTerm.length >= (options?.minSearchLength ?? 2)

  const totalResults = filteredResults.length

  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredResults])

  const handleClear = () => {
    setSearchInput('')
    inputRef.current?.focus()
  }

  const handleResultClick = (entry: GeckoEntry) => {
    onResultClick?.(entry)
    setIsFocused(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showResults || totalResults === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, totalResults - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredResults[selectedIndex]) {
          handleResultClick(filteredResults[selectedIndex].entry)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsFocused(false)
        inputRef.current?.blur()
        break
    }
  }

  useEffect(() => {
    if (resultsRef.current[selectedIndex]) {
      resultsRef.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [selectedIndex])

  const handleInputFocus = () => {
    setIsFocused(true)
  }

  const handleInputBlur = () => {
    // Don't close immediately - give click events time to process
    setTimeout(() => setIsFocused(false), 200)
  }

  const handleDropdownMouseDown = (e: React.MouseEvent) => {
    // Prevent input from losing focus when clicking in the dropdown
    e.preventDefault()
  }

  // Clear and blur when resetSignal changes (e.g., on route change)
  useEffect(() => {
    setSearchInput('')
    setIsFocused(false)
    inputRef.current?.blur()
  }, [resetSignal])

  return (
    <SearchContainer>
      <SearchInputContainer
        theme={theme}
        $backgroundColor={containerStyles?.backgroundColor}
        $borderColor={containerStyles?.borderColor}
        $borderRadius={containerStyles?.borderRadius}
        $height={containerStyles?.height}
        $focusBorderColor={containerStyles?.focusBorderColor}>
        <SearchInput
          ref={inputRef}
          theme={theme}
          type='text'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          $padding={inputStyles?.padding}
          $fontSize={inputStyles?.fontSize}
          $color={inputStyles?.color}
          $placeholderColor={inputStyles?.placeholderColor}
        />
        {searchInput && !isDebouncing && !isLoading && (
          <ClearButton theme={theme} onClick={handleClear} type='button'>
            <LucideIcon name='X' size={18} />
          </ClearButton>
        )}
        {(isDebouncing || isLoading) && <LoadingSpinner theme={theme} />}
      </SearchInputContainer>

      <ResultsDropdown
        theme={theme}
        $isVisible={showResults}
        onMouseDown={handleDropdownMouseDown}>
        {totalResults > 0 ? (
          <>
            <CategoryFilters
              counts={counts}
              activeFilters={activeFilters}
              onToggleFilter={toggleFilter}
            />
            <ResultsContent>
              {filteredResults.map((result, index) => (
                <ResultItemWrapper
                  key={result.entry.id}
                  ref={(el) => {
                    if (el) resultsRef.current[index] = el
                  }}
                  $isSelected={index === selectedIndex}
                  onMouseEnter={() => setSelectedIndex(index)}>
                  <SearchResultItem
                    entry={result.entry}
                    onClick={handleResultClick}
                  />
                </ResultItemWrapper>
              ))}
            </ResultsContent>
          </>
        ) : (
          <EmptyState theme={theme}>
            {isLoading
              ? 'Searching...'
              : `No results found for "${debouncedSearchTerm}"`}
          </EmptyState>
        )}
      </ResultsDropdown>
    </SearchContainer>
  )
}
